import React from "react";
import styled from "styled-components";
import { FiMail, FiPlus, FiDownload, FiEdit2, FiTrash2, FiStar, FiChevronDown, FiUpload } from "react-icons/fi";
import {
    SettingsContainer,
    Header,
    PrimaryButton,
    SecondaryButton,
} from "./styles/shared";

const EmailTemplateSettings = ({
    courseTemplates,
    defaultTemplateId,
    setShowCreateModal,
    setEditForm,
    shareToPublicSaving,
    savingDefaultTemplate,
    onDefaultTemplateChange,
    onOpenEdit,
    onSetAsDefault,
    onOpenShareToPublic,
    onDeleteTemplate,
    onPullClick,
}) => {
    return (
        <SettingsContainer>
            <Header>
                <div>
                    <h2>Email templates</h2>
                    <p>Manage automated course emails and template library.</p>
                </div>
            </Header>

            <DefaultTemplateCard>
                <TemplateField>
                    <TemplateLabel>Default template</TemplateLabel>
                    <SelectWrapper>
                        <StyledSelect
                            value={defaultTemplateId || ""}
                            onChange={onDefaultTemplateChange}
                            disabled={courseTemplates.length === 0 || savingDefaultTemplate}
                        >
                            <option value="">
                                {courseTemplates.length === 0 ? "No templates available" : "Select default template"}
                            </option>
                            {courseTemplates.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.title || t.subject}
                                </option>
                            ))}
                        </StyledSelect>
                        <SelectIcon>
                            <FiChevronDown size={18} />
                        </SelectIcon>
                    </SelectWrapper>
                </TemplateField>
                <TemplateActions>
                    <SecondaryButton onClick={onPullClick}>
                        <FiDownload />
                        Pull from public templates
                    </SecondaryButton>
                    <PrimaryButton
                        onClick={() => {
                            setEditForm({ title: "", subject: "", body: "" });
                            setShowCreateModal(true);
                        }}
                    >
                        <FiPlus />
                        Create new template
                    </PrimaryButton>
                </TemplateActions>
            </DefaultTemplateCard>

            <TemplateGrid>
                {courseTemplates.length === 0 ? (
                    <EmptyState>
                        <FiMail size={40} />
                        <h3>No course templates yet</h3>
                        <p>Create your first template or import from the public library.</p>
                    </EmptyState>
                ) : (
                    courseTemplates.map((t) => (
                        <TemplateCard key={t.id}>
                            {t.id === defaultTemplateId && (
                                <DefaultBadge>
                                    <FiStar size={12} />
                                    Default
                                </DefaultBadge>
                            )}
                            <CardTitle>{t.title || "Untitled template"}</CardTitle>
                            <CardLabel>Subject</CardLabel>
                            <CardSubject>{t.subject || "No subject"}</CardSubject>
                            <CardLabel>Body</CardLabel>
                            <CardBody>
                                {t.body.length > 50 ? t.body.slice(0, 50) + "..." : t.body}
                            </CardBody>
                            <CardActions>
                                <ActionButton onClick={() => onOpenEdit(t)}>
                                    <FiEdit2 size={14} />
                                    Edit
                                </ActionButton>
                                <ActionButton onClick={() => onSetAsDefault(t.id)}>
                                    <FiStar size={14} />
                                    Make Default
                                </ActionButton>
                                <PrimaryActionButton onClick={() => onOpenShareToPublic(t.id)} disabled={shareToPublicSaving}>
                                    <FiUpload size={14} />
                                    Publish
                                </PrimaryActionButton>
                                <DangerActionButton onClick={() => onDeleteTemplate(t.id)}>
                                    <FiTrash2 size={14} />
                                    Delete
                                </DangerActionButton>
                            </CardActions>
                        </TemplateCard>
                    ))
                )}
            </TemplateGrid>
        </SettingsContainer>
    );
};

export default EmailTemplateSettings;

const DefaultTemplateCard = styled.div`
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    @media (min-width: 768px) {
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
    }
`;

const TemplateField = styled.div`
    flex: 1;
    max-width: 320px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const TemplateLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    text-align: left;
`;

const SelectWrapper = styled.div`
    position: relative;
`;

const StyledSelect = styled.select`
    width: 100%;
    height: 48px;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 0 40px 0 14px;
    background: #f8fafc;
    color: #111827;
    font-size: 14px;
    appearance: none;
    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.15);
    }
`;

const SelectIcon = styled.div`
    position: absolute;
    right: 12px;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    color: #9ca3af;
    pointer-events: none;
`;

const TemplateActions = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const TemplateGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const TemplateCard = styled.div`
    position: relative;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DefaultBadge = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    color: white;
    background: #f97316;
    border-radius: 6px;
`;

const CardTitle = styled.div`
    font-weight: 600;
    font-size: 18px;
    color: #111827;
    text-align: left;
    margin-bottom: 8px;
`;

const CardSubject = styled.div`
    font-size: 15px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    margin-bottom: 4px;
`;

const CardBody = styled.div`
    font-size: 14px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
`;

const CardLabel = styled.label`
    font-size: 10px;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    text-align: left;
`;

const CardActions = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-top: 12px;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 10px;
    border-radius: 10px;
    background: #f1f5f9;
    border: none;
    font-size: 12px;
    font-weight: 700;
    color: #374151;
    cursor: pointer;
    transition: all 0.15s ease;
    &:hover {
        background: rgba(147, 51, 234, 0.08);
        color: #9333ea;
    }
`;

const PrimaryActionButton = styled(ActionButton)`
    background: #9333ea;
    color: white;
    box-shadow: 0 6px 12px rgba(147, 51, 234, 0.25);
    &:hover {
        background: #7e22ce;
        color: white;
    }
`;

const DangerActionButton = styled(ActionButton)`
    &:hover {
        background: #fee2e2;
        color: #dc2626;
    }
`;

const EmptyState = styled.div`
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    border: 2px dashed #e5e7eb;
    border-radius: 16px;
    text-align: center;
    color: #6b7280;
    h3 {
        margin-top: 12px;
        font-size: 18px;
        color: #111827;
        margin-bottom: 12px;
    }
`;
